/**
 * httpHelpers — small pure utilities for serviceLayerClient.
 *
 * Extracted from serviceLayerClient.ts 2026-04-24 for guard-file-size
 * compliance. No business logic lives here; everything is trivially
 * testable in isolation.
 */

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function withJitter(baseMs: number): number {
  return Math.floor(baseMs * (0.5 + Math.random()));
}

export function generateRequestId(): string {
  // Prefer crypto.randomUUID if available
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch { /* noop */ }
  return `req_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

export function getActorEmailFromGlobal(): string | null {
  try {
    type GlobalWithUser = { __USER__?: { email?: string } };
    const g = globalThis as GlobalWithUser;
    const email =
      g.__USER__?.email ||
      (typeof window !== 'undefined' ? (window as unknown as GlobalWithUser).__USER__?.email : null);
    return typeof email === 'string' && email.includes('@') ? email : null;
  } catch {
    return null;
  }
}

export function headersToObject(headers: Headers): Record<string, string> {
  const out: Record<string, string> = {};
  headers.forEach((value, key) => { out[key] = value; });
  return out;
}

/** The property a thrown HTTP error carries its parsed Retry-After (seconds) under. */
const RETRY_AFTER_SECONDS_KEY = 'retryAfterSeconds';
/** How deep `retryAfterSecondsOf` follows `.cause` (withWrappedError → FinalRefusalError → …). */
const MAX_CAUSE_DEPTH = 5;

/**
 * Record the server's Retry-After on the error serviceFetch throws, so the outbox can honour it
 * as the floor of the next attempt (offline/replayFailure.ts). Returns the same error.
 */
export function attachRetryAfterSeconds<E extends object>(err: E, seconds: number): E {
  Object.defineProperty(err, RETRY_AFTER_SECONDS_KEY, { value: seconds, enumerable: false });
  return err;
}

/**
 * The Retry-After (seconds) a failed call carries, following the `.cause` chain the domain
 * wrappers keep (missionVisits.helpers.withWrappedError, offlineQueueTypes.FinalRefusalError);
 * undefined when the server sent none.
 */
export function retryAfterSecondsOf(err: unknown): number | undefined {
  let current: unknown = err;
  for (let depth = 0; depth <= MAX_CAUSE_DEPTH; depth++) {
    if (current === null || typeof current !== 'object') return undefined;
    const value = (current as Record<string, unknown>)[RETRY_AFTER_SECONDS_KEY];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    current = (current as { cause?: unknown }).cause;
  }
  return undefined;
}

export function parseRetryAfterSeconds(raw: string | undefined): number | undefined {
  if (!raw) return undefined;
  const asNum = Number(raw);
  if (Number.isFinite(asNum)) return Math.max(0, Math.round(asNum));
  const asDateMs = Date.parse(raw);
  if (!Number.isNaN(asDateMs)) {
    return Math.max(0, Math.round((asDateMs - Date.now()) / 1000));
  }
  return undefined;
}
